import { useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { checkRediInvitationApi, sendRediInvitationApi, reportRediPopupDisplayedApi } from "../../api/api";
import SuccessMessage from "../customComponent/customMessages/SuccessMessage";
import ErrorMessage from "../customComponent/customMessages/ErrorMessage";
import {
  MPD_CONTACT_SECTION_HASH,
  REDI_ACTION_TYPES,
  REDI_ASSISTANCE_PROGRAM_PID,
  REDI_SKIP_REASONS,
  RETRIGGER_REDI_AFTER_EDIT_PARAM,
  RETRIGGER_REDI_PARAM
} from "../../constant/redi";

export const getRediLearnMorePath = ({ patientId, journeyId, potentialSavings }) => {
  const rediPotentialSaving = Array.isArray(potentialSavings)
    ? potentialSavings.find((ps) => ps.assistanceProgramPid === REDI_ASSISTANCE_PROGRAM_PID)
    : null;

  if (!rediPotentialSaving?.id) return null;

  return `/patient/${patientId}/journey/${journeyId}/optimizations/${rediPotentialSaving.id}`;
};

const useRediInvitation = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const [showRediModal, setShowRediModal] = useState(false);
  const [rediInvitationData, setRediInvitationData] = useState(null);
  const rediInvitationDataRef = useRef(null);
  rediInvitationDataRef.current = rediInvitationData;
  /** True only after popup-displayed API succeeded; must be true before allowing send-invitation. */
  const rediPopupDisplayRecordedRef = useRef(false);

  const openRediModal = useCallback(
    ({ prescriptionId, link, contactInfo, papId, drugName, reason, missingFields }, trigger = null) => {
      rediPopupDisplayRecordedRef.current = false;
      setRediInvitationData({ prescriptionId, link, contactInfo, papId, drugName, trigger, reason, missingFields });
      setShowRediModal(true);
    },
    []
  );

  /**
   * Cleans up the Redi retrigger URL params and fires the check-and-prepare API.
   * Shared between PatientSingleApplication and PatientTabs – both handle the same
   * "user returns from MPD" scenario but with different guard conditions.
   *
   * @param {URLSearchParams} searchParams     - mutable URLSearchParams from location.search
   * @param {boolean}         hasEditRetrigger - true when RETRIGGER_REDI_AFTER_EDIT_PARAM was set
   * @param {string|number}   patientId
   * @param {string|number}   journeyId
   */
  const triggerRediCheckOnReturn = useCallback(
    async ({ searchParams, hasEditRetrigger, patientId, journeyId }) => {
      searchParams.delete(RETRIGGER_REDI_PARAM);
      searchParams.delete(RETRIGGER_REDI_AFTER_EDIT_PARAM);
      const newSearch = searchParams.toString();
      history.replace({ search: newSearch ? `?${newSearch}` : "" });

      const action = hasEditRetrigger ? REDI_ACTION_TYPES.CONTACT_EDIT_RETURN : REDI_ACTION_TYPES.STATUS_CHANGE;

      try {
        const response = await checkRediInvitationApi(patientId, journeyId, action);
        if (response.shouldShowModal) {
          openRediModal(response, action);
        }
      } catch {
        ErrorMessage(t("errorMessages.failed_to_check_redi_invitation"));
      }
    },
    [history, openRediModal, t]
  );

  const handleRediModalDisplayed = useCallback(() => {
    const data = rediInvitationDataRef.current;

    const shouldSkipRecord =
      !data?.prescriptionId ||
      data?.reason === REDI_SKIP_REASONS.MISSING_CONTACT_INFO ||
      // CONTACT_EDIT_RETURN is a continuation of the same flow – the user simply had to update
      // contact info before completing. Don't increment popupDisplayCount or lastPopupDisplayedAt
      // again; just allow sending by marking the ref as satisfied.
      data?.trigger === REDI_ACTION_TYPES.CONTACT_EDIT_RETURN;

    if (shouldSkipRecord) {
      // Allow send-invitation to proceed without requiring a fresh popup-displayed call.
      if (data?.trigger === REDI_ACTION_TYPES.CONTACT_EDIT_RETURN) {
        rediPopupDisplayRecordedRef.current = true;
      }
      return;
    }

    reportRediPopupDisplayedApi(data.prescriptionId)
      .then(() => {
        rediPopupDisplayRecordedRef.current = true;
      })
      .catch(() => {
        rediPopupDisplayRecordedRef.current = false;
      });
  }, []);

  const handleRediModalClose = () => {
    rediPopupDisplayRecordedRef.current = false;
    setShowRediModal(false);
    setRediInvitationData(null);
  };

  const handleRediSavePreferences = async (preferences) => {
    try {
      if (!preferences.email && !preferences.sms) {
        ErrorMessage(t("redi.communication_settings.at_least_one_required"));
        return;
      }

      const data = rediInvitationDataRef.current;
      const requiredRecordDisplay = data?.prescriptionId && data?.reason !== REDI_SKIP_REASONS.MISSING_CONTACT_INFO;
      if (requiredRecordDisplay && !rediPopupDisplayRecordedRef.current) {
        ErrorMessage(t("redi.popup_record_failed_cannot_send"));
        return;
      }

      const response = await sendRediInvitationApi(rediInvitationData.prescriptionId, preferences);

      setShowRediModal(false);
      setRediInvitationData(null);

      const hasSuccessfulChannels = response?.channels?.length > 0;
      const hasErrors = response?.errors?.length > 0;

      if (hasSuccessfulChannels && !hasErrors) {
        SuccessMessage(t("redi.invitation_sent_success"));
      } else if (hasSuccessfulChannels && hasErrors) {
        const successChannels = response.channels.map((c) => c.toUpperCase()).join(", ");
        const failureDetails = response.errors.join("; ");
        ErrorMessage(t("redi.invitation_sent_partial", { successChannels, failureDetails }));
      } else {
        ErrorMessage(t("redi.invitation_send_error"));
      }
    } catch {
      ErrorMessage(t("redi.invitation_send_error"));
    }
  };

  /**
   * Navigate to the MPD Contact Details section.
   *
   * The current pathname (with the `retriggerRedi=true` query param appended) is stored
   * in `history.state.path` so the MPD `goBackToPatient` handler can navigate back to it
   * after the form is saved. That in turn causes PatientSingleApplication to detect the
   * param and re-call `check-and-prepare` (now that contact info has been added).
   *
   * @param {number|string} patientId
   * @param {number|string} journeyId
   */
  const handleAddMissingContact = (patientId, journeyId) => {
    setShowRediModal(false);
    setRediInvitationData(null);

    // Build the "return to" path with the retrigger flag so the Redi popup fires again
    // when the user comes back from MPD.
    const currentPathname = location.pathname;
    const returnPath = `${currentPathname}?${RETRIGGER_REDI_PARAM}=true`;

    const mpdPath = `/patient/${patientId}/journey/${journeyId}/edit${MPD_CONTACT_SECTION_HASH}`;

    history.push(mpdPath, { path: returnPath, scrollToSection: "manage-patient-contact-details" });
  };

  return {
    showRediModal,
    rediInvitationData,
    openRediModal,
    handleRediModalClose,
    handleRediSavePreferences,
    handleAddMissingContact,
    handleRediModalDisplayed,
    triggerRediCheckOnReturn
  };
};

export default useRediInvitation;
